import { useEffect, useRef, useState } from 'react';
import { useLocation } from 'react-router-dom';

import DeliveryLocationModal from '../Modals/DeliveryLocationModal';
import GuestDeliveryFFLModal from '../Modals/GuestDeliveryFFLModal';

import LiveUpdatesSection from './LiveUpdatesSection';
import MegaMenuSection from './MegaMenuSection';
import MiddleSection from './MiddleSection';
import MobileHeaderSection from './MobileHeaderSection';
import TopSection from './TopSection';
import '../../../assets/scss/Navbar.scss';

const Navbar = () => {
  const location = useLocation();
  const isHomePage = location.pathname === '/';
  const isFflDetail = location.pathname.startsWith('/ffl/');

  // below `lg` the mobile header is shown and it is always solid (no transparent-at-top state)
  const [isMobile, setIsMobile] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 991.98px)').matches
  );
  useEffect(() => {
    const mql = window.matchMedia('(max-width: 991.98px)');
    const onChange = (e: MediaQueryListEvent) => setIsMobile(e.matches);
    mql.addEventListener('change', onChange);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const isTransparentPage = (isHomePage || isFflDetail) && !isMobile;

  const [isScrolled, setIsScrolled] = useState(false);
  const [isVisible, setIsVisible] = useState(true);
  const [headerHeight, setHeaderHeight] = useState(0);
  const headerRef = useRef<HTMLDivElement>(null);

  // Measure header height and handle window resizing
  useEffect(() => {
    const handleResize = () => {
      if (headerRef.current) {
        const height = headerRef.current.offsetHeight;
        setHeaderHeight(height);
        // exposed for transparent pages, whose hero sits under the fixed header
        // and must pad its content below it (e.g. the FFL detail hero on mobile)
        document.documentElement.style.setProperty('--customer-header-height', `${height}px`);
      }
    };

    handleResize();

    let resizeObserver: ResizeObserver | null = null;
    if (headerRef.current && typeof ResizeObserver !== 'undefined') {
      resizeObserver = new ResizeObserver(() => {
        handleResize();
      });
      resizeObserver.observe(headerRef.current);
    }

    window.addEventListener('resize', handleResize);
    return () => {
      window.removeEventListener('resize', handleResize);
      if (resizeObserver) {
        resizeObserver.disconnect();
      }
    };
  }, []);

  // Monitor scroll behavior to show/hide the header and strip/apply glass styling
  useEffect(() => {
    let lastScrollY = window.scrollY;

    const handleScroll = () => {
      const currentScrollY = window.scrollY;

      // 1. Solid background threshold (e.g. past 50px)
      if (currentScrollY > 50) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }

      // 2. Hiding threshold (only hide once the placeholder is out of the viewport)
      const hideThreshold = isTransparentPage ? 50 : headerHeight;
      if (currentScrollY > hideThreshold) {
        const diff = currentScrollY - lastScrollY;
        if (diff > 10) {
          // Scrolling down -> hide header
          setIsVisible(false);
          lastScrollY = currentScrollY;
        } else if (diff < -10) {
          // Scrolling up -> show header
          setIsVisible(true);
          lastScrollY = currentScrollY;
        }
      } else {
        // Near or at the top -> always show header
        setIsVisible(true);
        lastScrollY = currentScrollY;
      }
    };

    // Initialize/sync on mount
    handleScroll();

    window.addEventListener('scroll', handleScroll, { passive: true });
    return () => window.removeEventListener('scroll', handleScroll);
  }, [isTransparentPage, headerHeight]);

  // Determine classes to apply
  const isGlass = isTransparentPage && !isScrolled;
  const headerClasses = [
    'customer-header-wrapper',
    isTransparentPage && !isScrolled ? 'is-transparent' : '',
    isGlass ? 'is-glass' : '',
    isScrolled ? 'is-scrolled' : '',
    !isVisible ? 'is-hidden' : '',
  ]
    .filter(Boolean)
    .join(' ');

  return (
    <>
      <div
        className="customer-header-placeholder"
        style={{ height: isTransparentPage ? 0 : headerHeight }}
      />
      <div ref={headerRef} className={headerClasses}>
        <div className="d-none d-lg-block">
          <TopSection />
          <MiddleSection isTransparent={isGlass} />
          <MegaMenuSection />
        </div>
        <MobileHeaderSection />
        <div className="d-none d-lg-block">
          <LiveUpdatesSection />
        </div>
      </div>
      {/* single instance shared by every "Deliver to" / selected FFL trigger */}
      <DeliveryLocationModal />
      {/* logged-out counterpart — picks the delivery location off the FFL map */}
      <GuestDeliveryFFLModal />
    </>
  );
};

export default Navbar;
